/**
 * ClusteredMarkerLayer - Wraps markers in react-leaflet-cluster for performance
 * Only used for "general" relevance places when "Show more places" is enabled
 */

import { Marker, Popup, Tooltip } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L, { type LatLngExpression } from 'leaflet';
import { createMemoizedPlaceIcon, createPlaceIcon } from './map-icon-utils';
import { PlaceMarkerPopup } from './PlaceMarkerPopup';

interface PlaceMarkerData {
    entity: GeographicEntityWithRelevance;
    position: LatLngExpression;
}

interface ClusteredMarkerLayerProps {
    markers: PlaceMarkerData[];
    bookName?: string;
    passageYear?: number;
    markerRefs: React.MutableRefObject<Map<string, L.Marker>>;
    isMobile: boolean;
    onMobileMarkerClick: (entity: GeographicEntityWithRelevance) => void;
    showLabels?: boolean;
}

export function ClusteredMarkerLayer({
    markers,
    bookName,
    passageYear,
    markerRefs,
    isMobile,
    onMobileMarkerClick,
    showLabels = true
}: ClusteredMarkerLayerProps) {
    if (markers.length === 0) return null;

    return (
        <MarkerClusterGroup
            chunkedLoading
            maxClusterRadius={50}
            spiderfyOnMaxZoom={true}
            showCoverageOnHover={false}
            zoomToBoundsOnClick={true}
            disableClusteringAtZoom={15}
            // Custom cluster icon styling - prevents small clusters
            iconCreateFunction={(cluster) => {
                const count = cluster.getChildCount();

                // Don't cluster if only 2 markers - return null to show individual markers
                if (count <= 2) {
                    return L.divIcon({ html: '', className: 'hidden-cluster' });
                }

                let size = 'small';
                let dimension = 30;

                if (count > 50) {
                    size = 'large';
                    dimension = 50;
                } else if (count > 20) {
                    size = 'medium';
                    dimension = 40;
                }

                return new L.DivIcon({
                    html: `<div class="cluster-icon cluster-${size}">
                        <span>${count}</span>
                    </div>`,
                    className: 'custom-cluster-icon',
                    iconSize: new L.Point(dimension, dimension)
                });
            }}
        >
            {markers.map((marker) => {
                return (
                    <Marker
                        key={`general-${marker.entity.placeName}`}
                        position={marker.position}
                        icon={createMemoizedPlaceIcon(marker.entity)}
                        ref={(ref) => {
                            if (ref) {
                                markerRefs.current.set(marker.entity.placeName, ref);
                            }
                        }}
                        eventHandlers={{
                            click: () => {
                                if (isMobile) {
                                    onMobileMarkerClick(marker.entity);
                                }
                            }
                        }}
                    >
                        {!isMobile && (
                            <Popup maxWidth={320} maxHeight={400} offset={[0, -10]}>
                                <PlaceMarkerPopup
                                    entity={marker.entity}
                                    bookName={bookName}
                                    passageYear={passageYear}
                                />
                            </Popup>
                        )}
                        {/* Subtle permanent label for general places */}
                        {showLabels && !['region', 'body-of-water', 'desert'].includes(marker.entity.type) && (
                            <Tooltip
                                permanent
                                direction="top"
                                offset={[0, -15]}
                                opacity={1}
                            >
                                <div style={{
                                    fontSize: marker.entity.type === 'body-of-water' ? '11px' : '9px',
                                    fontWeight: marker.entity.type === 'body-of-water' ? 500 : 400,
                                    color: marker.entity.type === 'body-of-water' ? '#3b82f6' : '#9ca3af',
                                    whiteSpace: 'nowrap',
                                    textShadow: marker.entity.type === 'body-of-water'
                                        ? '0 0 3px white, 0 0 6px white, 1px 1px 2px rgba(0,0,0,0.3)'
                                        : 'none'
                                }}>
                                    {marker.entity.placeName}
                                </div>
                            </Tooltip>
                        )}
                    </Marker>
                );
            })}
        </MarkerClusterGroup >
    );
}
